import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Container,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  FolderOpen as ApplicationsIcon,
  AutoFixHigh as AutofillIcon,
  Description as ResumeIcon,
  Hub as SourcesIcon,
  MarkEmailReadOutlined as TrackingIcon,
  WorkspacePremiumOutlined as PlanIcon,
  HelpOutline as HelpIcon,
  BookmarkBorder as SavedJobsIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../store/authStore';
import Logo from '../brand/Logo';

interface NavLink {
  label: string;
  to: string;
  icon: React.ReactElement;
  protected: boolean;
  /** Shown to staff users only. */
  staff?: boolean;
}

const NAV_LINKS: NavLink[] = [
  { label: 'Jobs', to: '/', icon: <WorkIcon />, protected: false },
  { label: 'Dashboard', to: '/dashboard', icon: <DashboardIcon />, protected: true },
  { label: 'Applications', to: '/applications', icon: <ApplicationsIcon />, protected: true },
  { label: 'Answers', to: '/answers', icon: <AutofillIcon />, protected: true },
  { label: 'Resumes', to: '/resumes', icon: <ResumeIcon />, protected: true },
  { label: 'Email tracking', to: '/email-tracking', icon: <TrackingIcon />, protected: true },
  { label: 'Profile', to: '/profile', icon: <PersonIcon />, protected: true },
  { label: 'How it works', to: '/help', icon: <HelpIcon />, protected: false },
  { label: 'Admin', to: '/admin', icon: <SourcesIcon />, protected: true, staff: true },
];

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuthStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const isStaff = isAuthenticated && user?.is_staff === true;
  const visibleLinks = NAV_LINKS.filter((l) => (!l.protected || isAuthenticated) && (!l.staff || isStaff));

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const go = (to: string) => {
    navigate(to);
    setDrawerOpen(false);
    handleClose();
  };

  const handleLogout = async () => {
    handleClose();
    setDrawerOpen(false);
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
      navigate('/');
    }
  };

  const initial = user?.firstName?.[0] || user?.username?.[0] || 'U';

  return (
    <>
      <AppBar position="sticky" sx={{ bgcolor: 'white', color: 'black', boxShadow: 1 }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Box
              component={RouterLink}
              to="/"
              aria-label="JobFlo: the job board"
              sx={{ display: 'inline-flex', alignItems: 'center', mr: 4, textDecoration: 'none' }}
            >
              <Logo size={30} />
            </Box>

            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 1 }}>
              {visibleLinks.map((link) => (
                <Button
                  key={link.to}
                  onClick={() => navigate(link.to)}
                  sx={{
                    color: isActive(link.to) ? 'primary.main' : 'text.primary',
                    fontWeight: isActive(link.to) ? 700 : 500,
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              {isAuthenticated ? (
                <>
                  <IconButton onClick={handleMenu} aria-label="Account menu">
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                      {initial.toUpperCase()}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        {user?.email}
                      </Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => go('/profile')}>
                      <PersonIcon sx={{ mr: 1 }} fontSize="small" />
                      Profile
                    </MenuItem>
                    <MenuItem onClick={() => go('/?saved=1&posted_within=any&agencies=show')}>
                      <SavedJobsIcon sx={{ mr: 1 }} fontSize="small" />
                      Saved jobs
                    </MenuItem>
                    <MenuItem onClick={() => go('/plan')}>
                      <PlanIcon sx={{ mr: 1 }} fontSize="small" />
                      Plan
                    </MenuItem>
                    <MenuItem onClick={() => go('/answers')}>
                      <AutofillIcon sx={{ mr: 1 }} fontSize="small" />
                      Answers
                    </MenuItem>
                    <MenuItem onClick={() => go('/resumes')}>
                      <ResumeIcon sx={{ mr: 1 }} fontSize="small" />
                      Resumes
                    </MenuItem>
                    <MenuItem onClick={() => go('/email-tracking')}>
                      <TrackingIcon sx={{ mr: 1 }} fontSize="small" />
                      Email tracking
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout} disabled={loggingOut}>
                      <LogoutIcon sx={{ mr: 1 }} fontSize="small" />
                      {loggingOut ? 'Logging out...' : 'Logout'}
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
                  <Button
                    onClick={() => navigate('/login')}
                    sx={{ color: 'text.primary', fontWeight: 500 }}
                  >
                    Login
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/register')}
                    sx={{ fontWeight: 600 }}
                  >
                    Sign Up
                  </Button>
                </Box>
              )}

              <IconButton
                sx={{ display: { md: 'none' } }}
                onClick={() => setDrawerOpen(true)}
                aria-label="Open navigation"
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile navigation drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 260 } }}
      >
        <List>
          {visibleLinks.map((link) => (
            <ListItemButton
              key={link.to}
              selected={isActive(link.to)}
              onClick={() => go(link.to)}
            >
              <ListItemIcon>{link.icon}</ListItemIcon>
              <ListItemText primary={link.label} />
            </ListItemButton>
          ))}
          <Divider sx={{ my: 1 }} />
          {isAuthenticated ? (
            <ListItemButton onClick={handleLogout} disabled={loggingOut}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={loggingOut ? 'Logging out...' : 'Logout'} />
            </ListItemButton>
          ) : (
            <>
              <ListItemButton onClick={() => go('/login')}>
                <ListItemText primary="Login" />
              </ListItemButton>
              <ListItemButton onClick={() => go('/register')}>
                <ListItemText primary="Sign Up" />
              </ListItemButton>
            </>
          )}
        </List>
      </Drawer>
    </>
  );
};

export default Header;
